import {
  Box,
  Heading,
  Stack,
  List,
  ListItem,
  ListIcon,
  HStack,
  Text,
} from "@chakra-ui/react";
import React, { useContext } from "react";
import CurrencyContext from "../../context/CurrencyContext";
import { Trans } from "@lingui/macro";
import { FaCcMastercard, FaInfo } from "react-icons/fa";
import { ActivitySummary as ActivitySummaryType } from "../../types/activity.types";
import { MultiUserMessage } from "../common/MultiUserMessage";
import { formatDateForInput } from "../../utils/date-formatter";
import { formatPrice } from "../../utils/price-formatter";

interface ActivitySummaryProps {
  activitySummaries: ActivitySummaryType[];
  color: string;
  originalSummaries?: ActivitySummaryType[] | null;
}

export const ActivitySummary: React.FC<ActivitySummaryProps> = ({
  activitySummaries,
  color,
  originalSummaries,
}) => {
  const currency = useContext(CurrencyContext);
  const hasChanges = originalSummaries !== undefined;

  // Helper to find original activity by activityId
  const findOriginalActivity = (activity: ActivitySummaryType) => {
    if (!originalSummaries) return null;
    return originalSummaries.find(
      (orig) => orig.activityId === activity.activityId
    );
  };

  const hasAnyChanges = React.useMemo(() => {
    if (!hasChanges || !originalSummaries) return false;
    return activitySummaries.some((activity) => {
      const orig = findOriginalActivity(activity);
      const originalCount = orig?.choiceCount ?? 0;
      const countChanged = originalCount !== activity.choiceCount;
      const priceChanged = !!orig && orig.price !== activity.price;
      return (
        countChanged ||
        priceChanged
      );
    });
  }, [hasChanges, originalSummaries, activitySummaries, findOriginalActivity]);

  return (
    <Box
      borderRadius={hasAnyChanges ? "lg" : "none"}
      _before={
        hasAnyChanges
          ? {
              content: '""',
              position: "absolute",
              inset: "0px",
              zIndex: -1,
              borderRadius: "lg",
              filter: "blur(4px)",
              pointerEvents: "none",
              boxShadow: `
                -3px -3px 8px rgba(245, 101, 101, 0.15),
                1.5px -2px 9px rgba(237, 100, 166, 0.15),
                -4.5px  1.5px 10px rgba(237, 137,  54, 0.15),
                1.5px  3px 8px rgba(237, 100, 166, 0.125),
                -3px 5.5px 8px rgba(245, 101, 101, 0.125),
                3px -3px 9px rgba(237, 137,  54, 0.125)
              `,
            }
          : undefined
      }
      zIndex={0}
      position="relative"
    >
      {activitySummaries.map((activity) => {
        const originalActivity = findOriginalActivity(activity);
        const originalCount = originalActivity?.choiceCount ?? 0;
        const isNewActivity =
          hasChanges && !originalActivity && activity.choiceCount > 0;
        const countChanged =
          hasChanges &&
          originalCount !== activity.choiceCount;
        const priceChanged =
          hasChanges &&
          originalActivity &&
          originalActivity.price !== activity.price;
        const totalPriceChanged = countChanged || priceChanged || isNewActivity;

        return (
          <Stack
            px={0}
            py={3}
            justifyContent={{
              base: "flex-start",
              md: "space-between",
            }}
            direction={{
              base: "column",
              md: "row",
            }}
            alignItems={{ md: "center" }}
            key={activity.activityType + activity.activityId}
          >
            <Heading size={"md"} w={{ md: "34%" }}>
              {activity.name}{" "}
              <span
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {formatDateForInput(activity.date)}
              </span>
            </Heading>
            <List spacing={3} textAlign="start" w={{ md: "40%" }}>
              <ListItem alignItems={"baseline"}>
                <HStack gap={0}>
                  <ListIcon as={FaCcMastercard} color={`${color}.500`} />
                  <Text>
                    {priceChanged || isNewActivity ? (
                      <>
                        <Text as="span" color="green.500" fontWeight={600}>
                          {formatPrice(activity.price)} {currency} /{" "}
                          <Trans>person</Trans>
                        </Text>
                        {originalActivity ? (
                          <Text
                            as="span"
                            color="red.500"
                            ml={2}
                            textDecoration="line-through"
                          >
                            ({formatPrice(originalActivity.price)} {currency} /{" "}
                            <Trans>person</Trans>)
                          </Text>
                        ) : (
                          <Text
                            as="span"
                            color="red.500"
                            ml={2}
                            textDecoration="line-through"
                          >
                            (-)
                          </Text>
                        )}
                      </>
                    ) : (
                      <>
                        {formatPrice(activity.price)} {currency} /{" "}
                        <Trans>person</Trans>
                      </>
                    )}
                  </Text>
                </HStack>
              </ListItem>
              <ListItem alignItems={"baseline"}>
                <HStack gap={0}>
                  <ListIcon as={FaInfo} color={`${color}.500`} />
                  <Text>
                    {countChanged || isNewActivity ? (
                      <>
                        <Text as="span" color="green.500" fontWeight={600}>
                          <MultiUserMessage count={activity.choiceCount} />
                        </Text>
                        {originalActivity ? (
                          <Text
                            as="span"
                            color="red.500"
                            ml={2}
                            textDecoration="line-through"
                          >
                            (
                            <MultiUserMessage
                              count={originalActivity.choiceCount}
                            />
                            )
                          </Text>
                        ) : (
                          <Text
                            as="span"
                            color="red.500"
                            ml={2}
                            textDecoration="line-through"
                          >
                            (-)
                          </Text>
                        )}
                      </>
                    ) : (
                      <MultiUserMessage count={activity.choiceCount} />
                    )}
                  </Text>
                </HStack>
              </ListItem>
            </List>
            <Heading size={"xl"} w={{ md: "40%" }} textAlign={{ md: "end" }}>
              {totalPriceChanged ? (
                <>
                  <Text as="span" color="green.500" fontWeight={600}>
                    {formatPrice(activity.price * activity.choiceCount)}{" "}
                    {currency}
                  </Text>
                  {originalActivity ? (
                    <Text
                      as="span"
                      color="red.500"
                      ml={2}
                      textDecoration="line-through"
                      fontSize="lg"
                    >
                      (
                      {formatPrice(
                        originalActivity.price * originalActivity.choiceCount
                      )}{" "}
                      {currency})
                    </Text>
                  ) : (
                    <Text
                      as="span"
                      color="red.500"
                      ml={2}
                      textDecoration="line-through"
                      fontSize="lg"
                    >
                      (0 {currency})
                    </Text>
                  )}
                </>
              ) : (
                <>
                  {formatPrice(activity.price * activity.choiceCount)}{" "}
                  {currency}
                </>
              )}
            </Heading>
          </Stack>
        );
      })}
    </Box>
  );
};
